<script setup lang="ts">
import PrizeCard from '@/components/PrizeCard.vue'
import { Duration } from 'luxon'
import BaseButton from '@/components/base/BaseButton.vue'
import { T } from '@tolgee/vue'
import { formatNumberSemiTruncate } from '@/helpers/utils'
import StrokeCoinImage from '@/assets/images/stroke-coin.png'
import { ECOMMERCE_ITEM_TAG, type EcommerceItem } from '@/types/shared/player-config'
import { computed } from 'vue'
import { useShop, useShopItem } from '@/composables/game/shop'
import BestOfferBadgeImage from '@/assets/images/periodic/best-offer-badge.png'
import PopularOfferBadgeImage from '@/assets/images/shop-popular-badge.png'
import { CARD_COLORS } from '@/enums/common'
import { twMerge } from 'tailwind-merge'

const props = withDefaults(defineProps<{
	item: EcommerceItem
	onlyVisual?: boolean
	showBadge?: boolean
}>(), {
  showBadge: true,
})

defineEmits<{
	(e: 'selectItem'): void
}>()

const {
  itemStarPrice,
  spinBenefit,
  goldenCoinBenefit,
  coinBenefit,
  tradingPerHourBenefit,
  virtualFriendBenefit,
  offlineProfitBenefit,
  specialCurrencyBenefit,
  hasSale,
} = useShopItem(computed(() => props.item))
const anyCoinBenefit = computed(() => goldenCoinBenefit.value > 0 ? goldenCoinBenefit.value : coinBenefit.value)
const {
  getItemDisabledSeconds,
  isSale,
  isDefaultBOGO,
  isDefaultBOGX,
  isBOGO,
  isBOGX,
  isCashback,
  isDiscount,
  saleValue,
} = useShop()
</script>
<template>
  <div class="relative flex">
    <PrizeCard
      card-class="border-[#545351]"
      :class="onlyVisual && 'pointer-events-none'"
      :color="CARD_COLORS.GRAY"
      :disabled="getItemDisabledSeconds(item) > 0 && !onlyVisual"
      @click="$emit('selectItem')"
    >
      <div
        v-if="showBadge && item.tags.includes(ECOMMERCE_ITEM_TAG.POPULAR)"
        class="absolute -left-8 -top-8 rotate-[-23deg] size-[100px] z-[1]"
      >
        <img
          :src="PopularOfferBadgeImage"
          class="w-full h-auto float"
        >
      </div>
      <div
        class="flex flex-col items-center pt-6 w-full h-full"
        :class="onlyVisual && 'justify-center'"
      >
        <div class="w-[48px] min-w-[48px] h-[63px] mb-1.5 object-contain">
          <img
            :src="item.imageUrl"
          >
        </div>

        <div class="flex flex-col items-center">
          <div
            v-if="anyCoinBenefit"
            class="flex items-center gap-x-1.5 text-solid-white"
          >
            <img
              :src="StrokeCoinImage"
              class="h-5 w-auto object-contain"
            >
            <span class="text-xl font-semibold -tracking-[0.24px] black-stroke">{{
              formatNumberSemiTruncate(anyCoinBenefit)
            }}</span>
          </div>
          <span
            v-if="(anyCoinBenefit) && spinBenefit"
            class="-my-[7px] text-xl text-white font-semibold -tracking-[0.28px] black-stroke"
          >+</span>

          <span
            v-if="spinBenefit"
            class="text-xl font-semibold text-solid-white -tracking-[0.24px] black-stroke"
          >
            <T
              key-name="fortune_wheel.info.more_spins.count_spin"
              default-value="{count, plural, one {# spin} other {# spins}}"
              :params="{count: spinBenefit}"
            />
          </span>
          <div
            v-if="tradingPerHourBenefit"
            class="flex flex-col items-center"
          >
            <span class="uppercase text-xl font-bold black-stroke">
              <T
                key-name="shop.hour"
                default-value="{count, plural, one {# hour} other {# hours}}"
                :params="{count: tradingPerHourBenefit}"
              />

            </span>
            <span class="text-base font-semibold black-stroke">
              <T
                key-name="shop.card.trading_per_hour"
                default-value="Trading per hour x2"
              />
            </span>
          </div>
          <div
            v-if="offlineProfitBenefit"
            class="flex flex-col items-center"
          >
            <span class="uppercase text-xl font-bold black-stroke">{{ `${offlineProfitBenefit} hours` }}</span>
            <span class="text-base font-semibold black-stroke">
              <T
                key-name="shop.card.offline_profit"
                default-value="Offline profit"
              />
            </span>
          </div>
          <div
            v-if="virtualFriendBenefit"
            class="flex flex-col items-center"
          >
            <span class="uppercase text-xl font-bold black-stroke">{{ virtualFriendBenefit }}</span>
            <span class="text-base font-semibold black-stroke">
              <T
                key-name="shop.card.virtual_friend"
                default-value="Virtual {count, plural, one {friend} other {friends}}"
                :params="{count: virtualFriendBenefit}"
              />
            </span>
          </div>
          <div
            v-if="specialCurrencyBenefit"
            class="text-xl font-semibold text-solid-white -tracking-[0.24px] black-stroke"
          >
            {{ item.name }}
          </div>
        </div>
        <div
          v-if="!onlyVisual"
          class="flex-1 h-full flex flex-col justify-end w-full"
        >
          <BaseButton
            v-if="!!getItemDisabledSeconds(item)"
            class="w-full"
            button-class="unslashed-zero px-3 h-[48px] text-xl whitespace-nowrap !border-white/40"
            disabled
          >
            {{ Duration.fromObject({ seconds: getItemDisabledSeconds(item) }).toFormat('hh:mm:ss') }}
          </BaseButton>
          <BaseButton
            v-else-if="itemStarPrice.amount === 0"
            button-class="px-3 h-[48px] text-xl"
            class="w-full"
            spinner-class="w-4 h-4 min-w-[16px]"
          >
            <T
              key-name="shop.free"
              default-value="Free"
            />
          </BaseButton>
          <div
            v-else
            class="w-full flex relative rounded-[13px] p-0.5"
            :class="!isSale && 'green-gradient'"
          >
            <BaseButton
              v-if="isBOGO || isBOGX"
              class="w-full"
              spinner-class="w-4 h-4 min-w-[16px]"
              :button-class="twMerge('px-3 h-[48px] text-xl border-0', isDefaultBOGO && 'default-BOGO-background-buttons', isDefaultBOGX && 'default-BOGX-background-buttons')"
            >
              <div :class="twMerge('flex items-center gap-x-2 ', (isDefaultBOGO || isDefaultBOGX) && 'text-black')">
                <span class="lowercase black-stroke-gold-text-3">x{{ isBOGO ? 2 : saleValue }}</span>
                <div :class="twMerge('w-[1px] h-4', (isDefaultBOGO || isDefaultBOGX) && 'bg-black/[50%]')" />
                <span>
                  {{ `$${itemStarPrice.usdAmount.toFixed(2)}` }}
                </span>
              </div>
            </BaseButton>
            <BaseButton
              v-else-if="isCashback"
              class="w-full"
              spinner-class="w-4 h-4 min-w-[16px]"
              button-class="px-3 h-[48px] text-xl default-cashback-background border-0 "
            >
              <div class="flex items-center gap-x-2 text-white">
                <span>
                  {{ `$${itemStarPrice.usdAmount.toFixed(2)}` }}
                </span>
              </div>
            </BaseButton>
            <BaseButton
              v-else
              style-type="green"
              class="w-full"
              spinner-class="w-4 h-4 min-w-[16px]"
              button-class="px-3 h-[48px] text-xl"
            >
              <div class="flex items-center gap-x-1">
                <span
                  v-if="isDiscount && hasSale"
                  class="text-base text-black/[44%] line-through"
                >
                  <span>{{ `$${itemStarPrice.originalUsdAmount.toFixed(2)}` }}</span>
                </span>
                <span>{{ `$${itemStarPrice.usdAmount.toFixed(2)}` }}</span>
              </div>
            </BaseButton>
          </div>
        </div>
      </div>
    </PrizeCard>
    <div
      v-if="item.tags.includes(ECOMMERCE_ITEM_TAG.BEST_OFFER)"
      class="absolute w-[110%] top-2 left-1/2 -translate-x-1/2"
    >
      <img
        :src="BestOfferBadgeImage"
        class="w-full"
      >
    </div>
  </div>
</template>
<style>
@import '@/assets/styles/sales.scss';

.black-stroke {
	text-shadow: -1.5px -1.5px 0 #000,
	1.5px -1.5px 0 #000,
	-1.5px 1.5px 0 #000,
	1.5px 1.5px 0 #000,
	-2px 0 0 #000,
	2px 0 0 #000,
	0 -2px 0 #000,
	0 2px 0 #000,
	-1px -1px 0 #000, /* Finer shadow adjustments */ 1px -1px 0 #000,
	-1px 1px 0 #000,
	1px 1px 0 #000;
}
</style>
